/** localStorage JSON map of local calendar days to game slugs played ≥ 1 minute. */
export const DAILY_PUZZLE_PROGRESS_KEY = "playmath:daily-puzzle-progress";

/** Same-tab signal so open Daily Puzzle views refresh after a play is recorded. */
export const DAILY_PUZZLE_PROGRESS_EVENT = "playmath:daily-puzzle-progress";

export const DAILY_PUZZLE_PLAY_THRESHOLD_MS = 60_000;

export interface DailyPuzzleProgress {
  /** YYYY-MM-DD → slugs played for at least one minute that local day. */
  days: Record<string, string[]>;
}

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Format a Date as a local calendar day key. */
export function toLocalDayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseProgress(raw: string | null): DailyPuzzleProgress {
  if (!raw) return { days: {} };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !("days" in parsed)) {
      return { days: {} };
    }
    const daysUnknown = (parsed as { days: unknown }).days;
    if (!daysUnknown || typeof daysUnknown !== "object") {
      return { days: {} };
    }
    const days: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(daysUnknown)) {
      if (!DAY_KEY_RE.test(key) || !Array.isArray(value)) continue;
      const slugs = [
        ...new Set(
          value.filter((slug): slug is string => typeof slug === "string" && slug.length > 0)
        ),
      ];
      days[key] = slugs;
    }
    return { days };
  } catch (error) {
    console.error("Error parsing daily puzzle progress", { error });
    return { days: {} };
  }
}

export function readDailyPuzzleProgress(): DailyPuzzleProgress {
  try {
    if (typeof window === "undefined") return { days: {} };
    return parseProgress(localStorage.getItem(DAILY_PUZZLE_PROGRESS_KEY));
  } catch (error) {
    console.error("Error reading daily puzzle progress", { error });
    return { days: {} };
  }
}

function persistProgress(progress: DailyPuzzleProgress): void {
  localStorage.setItem(DAILY_PUZZLE_PROGRESS_KEY, JSON.stringify(progress));
  window.dispatchEvent(new Event(DAILY_PUZZLE_PROGRESS_EVENT));
}

/**
 * Record that `slug` was played for at least one minute on the given local day.
 * Fail-soft if storage is unavailable. Idempotent per day+slug.
 */
export function recordDailyPuzzlePlay(slug: string, now: Date = new Date()): DailyPuzzleProgress {
  const trimmed = slug.trim();
  if (!trimmed) return readDailyPuzzleProgress();

  try {
    if (typeof window === "undefined") return { days: {} };
    const dayKey = toLocalDayKey(now);
    const progress = readDailyPuzzleProgress();
    const existing = progress.days[dayKey] ?? [];
    if (existing.includes(trimmed)) {
      return progress;
    }
    const next: DailyPuzzleProgress = {
      days: {
        ...progress.days,
        [dayKey]: [...existing, trimmed],
      },
    };
    persistProgress(next);
    return next;
  } catch (error) {
    console.error("Error recording daily puzzle play", { slug: trimmed, error });
    return readDailyPuzzleProgress();
  }
}

export function hasPlayedDailyPuzzleGame(
  progress: DailyPuzzleProgress,
  slug: string,
  dayKey: string
): boolean {
  return (progress.days[dayKey] ?? []).includes(slug);
}

/** True when at least one picker game was played ≥ 1 minute on that local day. */
export function hasFinishedDailyPuzzleDay(progress: DailyPuzzleProgress, dayKey: string): boolean {
  return (progress.days[dayKey] ?? []).length > 0;
}
